import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Eliminamos la importación de framer-motion
import { getProduct, createProduct, updateProduct, getCategories } from '../../config/back4app';
import ProductImageManager from '../../components/ProductImageManager';
import MedicalSpecialtySelector from '../../components/admin/MedicalSpecialtySelector';
import { FaTimes } from 'react-icons/fa';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    presentation: '',
    content: '',
    usage: '',
    ingredients: '',
    benefits: [],
    pricing: {
      retail: '',
      wholesale: '',
      distributor: '',
      moqOptions: []
    },
    status: 'active',
    image: null,
    medicalSpecialtiesRel: [],
    moq: 0
  });

  // Cargar datos del producto y categorías si estamos en modo edición
  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Error al cargar las categorías');
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const product = await getProduct(id);
      
      // Asegurar que pricing.moqOptions exista
      const pricing = product.pricing || {};
      if (!pricing.moqOptions) {
        // Si no existe, crearlo basado en el valor de moq
        if (product.moq === 200) {
          pricing.moqOptions = [200, 500]; // Productos con MOQ 200 generalmente tienen ambas opciones
        } else if (product.moq === 500) {
          pricing.moqOptions = [500]; // Productos con MOQ 500 solo tienen esa opción
        } else {
          pricing.moqOptions = product.moq ? [product.moq] : [];
        }
      }
      
      setFormData({
        ...product,
        pricing: pricing
      });
      
      if (product.categoryId) {
        setSelectedCategory({
          id: product.categoryId,
          name: product.categoryName
        });
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const productData = {
        ...formData,
        pricing: {
          retail: parseFloat(formData.pricing.retail) || 0,
          wholesale: parseFloat(formData.pricing.wholesale) || 0,
          distributor: parseFloat(formData.pricing.distributor) || 0,
          moqOptions: formData.pricing.moqOptions
        }
      };

      if (id) {
        await updateProduct(id, productData);
      } else {
        await createProduct(productData);
      }
      navigate('/admin/products');
    } catch (err) {
      console.error('Error saving product:', err);
      setError('Error al guardar el producto');
      setShowConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBenefitChange = (index, value) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData({ ...formData, benefits: newBenefits });
  };

  const addBenefit = () => {
    setFormData({
      ...formData,
      benefits: [...formData.benefits, '']
    });
  };

  const removeBenefit = (index) => {
    const newBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData({ ...formData, benefits: newBenefits });
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setFormData({ ...formData, category: categoryId });
    const category = categories.find(c => c.id === categoryId);
    setSelectedCategory(category);
  };

  const handleImageChange = (imageData) => {
    setFormData({ ...formData, image: imageData });
  };

  const handleMoqChange = (value) => {
    setFormData({ ...formData, moq: value });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        {/* Formulario Principal */}
        <div className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3 mb-0">{id ? 'Editar Producto' : 'Nuevo Producto'}</h1>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/admin/products')}
            >
              Cancelar
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Nombre</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Categoría</label>
                    <select
                      className="form-select"
                      value={formData.category}
                      onChange={handleCategoryChange}
                      required
                    >
                      <option value="">Seleccionar categoría</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label">Descripción</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Presentación</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.presentation}
                      onChange={(e) =>
                        setFormData({ ...formData, presentation: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                    >
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                    </select>
                  </div>

                  <div className="col-md-12">
                    <label className="form-label">Contenido</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.content}
                      onChange={(e) =>
                        setFormData({ ...formData, content: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Uso Recomendado</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.usage}
                      onChange={(e) =>
                        setFormData({ ...formData, usage: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Ingredientes</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={formData.ingredients}
                      onChange={(e) =>
                        setFormData({ ...formData, ingredients: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12 mb-3">
                    <label className="form-label">Especialidades Médicas</label>
                    <MedicalSpecialtySelector
                      value={formData.medicalSpecialtiesRel}
                      onChange={(selectedSpecialties) =>
                        setFormData({
                          ...formData,
                          medicalSpecialtiesRel: selectedSpecialties
                        })
                      }
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Beneficios</label>
                    {formData.benefits.map((benefit, index) => (
                      <div key={index} className="input-group mb-2">
                        <input
                          type="text"
                          className="form-control"
                          value={benefit}
                          onChange={(e) => handleBenefitChange(index, e.target.value)}
                          placeholder="Agregar beneficio"
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeBenefit(index)}
                        >
                          Eliminar
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={addBenefit}
                    >
                      Agregar Beneficio
                    </button>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Precio al por menor</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.pricing.retail}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              retail: e.target.value
                            }
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Precio al por mayor</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.pricing.wholesale}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              wholesale: e.target.value
                            }
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Precio distribuidor</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.pricing.distributor}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              distributor: e.target.value
                            }
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Cantidad Mínima de Orden (MOQ)</label>
                    <div className="d-flex flex-column">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="moq200"
                          checked={formData.pricing.moqOptions?.includes(200)}
                          onChange={(e) => {
                            const currentOptions = formData.pricing.moqOptions || [];
                            let newOptions;
                            if (e.target.checked) {
                              newOptions = [...currentOptions, 200].filter((v, i, a) => a.indexOf(v) === i);
                            } else {
                              newOptions = currentOptions.filter(option => option !== 200);
                            }
                            setFormData({
                              ...formData,
                              pricing: {
                                ...formData.pricing,
                                moqOptions: newOptions
                              },
                              moq: newOptions.length > 0 ? Math.min(...newOptions) : 0
                            });
                          }}
                        />
                        <label className="form-check-label" htmlFor="moq200">
                          200 unidades
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="moq500"
                          checked={formData.pricing.moqOptions?.includes(500)}
                          onChange={(e) => {
                            const currentOptions = formData.pricing.moqOptions || [];
                            let newOptions;
                            if (e.target.checked) {
                              newOptions = [...currentOptions, 500].filter((v, i, a) => a.indexOf(v) === i);
                            } else {
                              newOptions = currentOptions.filter(option => option !== 500);
                            }
                            setFormData({
                              ...formData,
                              pricing: {
                                ...formData.pricing,
                                moqOptions: newOptions
                              },
                              moq: newOptions.length > 0 ? Math.min(...newOptions) : 0
                            });
                          }}
                        />
                        <label className="form-check-label" htmlFor="moq500">
                          500 unidades
                        </label>
                      </div>
                      {formData.pricing.moqOptions?.length === 0 && (
                        <div className="text-danger mt-1">
                          Seleccione al menos una opción de MOQ
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 mt-4">
                    <button type="submit" className="btn btn-primary">
                      {id ? 'Actualizar' : 'Crear'} Producto
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar - Preview y Gestor de Imágenes */}
        <div className="col-lg-4">
          <ProductImageManager
            productName={formData.name}
            categoryName={selectedCategory?.name || ''}
            initialImage={formData.image}
            onImageChange={handleImageChange}
          />

          <div className="card mt-4">
            <div className="card-body">
              <h2 className="h5 mb-4">Vista Previa</h2>
              
              <div className="preview-card">
                <h3 className="h5 mb-2">{formData.name || 'Nombre del Producto'}</h3>
                <p className="text-muted mb-3">{formData.description || 'Descripción del producto'}</p>
                
                <div className="mb-3">
                  <strong>Presentación:</strong> {formData.presentation}<br />
                  <strong>Contenido:</strong> {formData.content}
                </div>

                <div className="mb-3">
                  <strong>Modo de Uso:</strong>
                  <p className="mb-2">{formData.usage}</p>
                </div>

                <div className="mb-3">
                  <strong>Ingredientes:</strong>
                  <p className="mb-2">{formData.ingredients}</p>
                </div>

                {formData.benefits.length > 0 && (
                  <div className="mb-3">
                    <strong>Beneficios:</strong>
                    <ul className="mb-0">
                      {formData.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mb-3">
                  <strong>Precios:</strong><br />
                  Retail: ${formData.pricing.retail}<br />
                  Wholesale: ${formData.pricing.wholesale}<br />
                  Distribuidor: ${formData.pricing.distributor}
                </div>

                <div className="mb-3">
                  <strong>Cantidad Mínima de Orden (MOQ):</strong> {formData.moq}
                </div>

                <div className="mt-2">
                  <span className={`badge bg-${formData.status === 'active' ? 'success' : 'secondary'}`}>
                    {formData.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación - simplificado sin animaciones */}
      {showConfirmation && (
        <>
          <div className="modal-backdrop show" />
          <div className="modal d-block">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmar {id ? 'Actualización' : 'Creación'} de Producto</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowConfirmation(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="preview-image mb-3">
                        {formData.image && (
                          <img
                            src={formData.image.url}
                            alt={formData.name}
                            className="img-fluid rounded"
                          />
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h4>{formData.name}</h4>
                      <p className="text-muted">{formData.description}</p>
                      
                      <div className="mb-2">
                        <strong>Categoría:</strong> {selectedCategory?.name}
                      </div>
                      
                      <div className="mb-2">
                        <strong>Presentación:</strong> {formData.presentation}
                      </div>
                      
                      <div className="mb-2">
                        <strong>Contenido:</strong> {formData.content}
                      </div>
                      
                      <div className="mb-2">
                        <strong>Precios:</strong><br />
                        <small>
                          Retail: ${formData.pricing.retail}<br />
                          Wholesale: ${formData.pricing.wholesale}<br />
                          Distribuidor: ${formData.pricing.distributor}
                        </small>
                      </div>

                      <div className="mb-2">
                        <strong>Cantidad Mínima de Orden (MOQ):</strong> {formData.moq}
                      </div>

                      <div className="mt-2">
                        <span className={`badge bg-${formData.status === 'active' ? 'success' : 'secondary'}`}>
                          {formData.status === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowConfirmation(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : 'Confirmar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductForm;
